import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  useCourse, 
  useEnrolledStudents, 
  useSessions, 
  useMaterials, 
  useGrades, 
  useCertificates,
  useEnrollStudent,
  useUnenrollStudent,
  useCreateSession,
  useDeleteSession,
  useCreateMaterial,
  useDeleteMaterial,
  useCreateGrade,
  useIssueCertificate,
  useStudents
} from '../../lib/hooks';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../../components/ui/dialog';
import { 
  ArrowLeft, 
  Loader2, 
  Users, 
  Calendar,
  Clock,
  BookOpen,
  FileText,
  Award,
  Plus,
  Trash2,
  Download,
  ExternalLink,
  Play,
  File,
  CheckCircle,
  XCircle,
  AlertCircle,
  UserPlus,
  Video,
  Upload
} from 'lucide-react';
import CourseForm from '../../components/courses/CourseForm';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const levelColors = {
  A1: 'bg-slate-200 text-slate-800',
  A2: 'bg-blue-100 text-blue-700',
  B1: 'bg-yellow-100 text-yellow-700',
  B2: 'bg-orange-100 text-orange-700',
  C1: 'bg-green-100 text-green-700',
  C2: 'bg-emerald-100 text-emerald-700',
};

const statusColors = {
  upcoming: 'bg-blue-100 text-blue-700',
  active: 'bg-success/10 text-success',
  completed: 'bg-slate-100 text-slate-600',
  cancelled: 'bg-destructive/10 text-destructive',
};

const statusLabelsFn = (lang = 'fr') => ({
  upcoming: lang === 'de' ? 'Bevorstehend' : 'À venir',
  active: lang === 'de' ? 'Aktiv' : 'Actif',
  completed: lang === 'de' ? 'Abgeschlossen' : 'Terminé',
  cancelled: lang === 'de' ? 'Abgebrochen' : 'Annulé',
});

const materialTypeIcons = {
  pdf: File,
  video: Play,
  exercise: FileText,
  link: ExternalLink,
  other: FileText,
};

const CourseDetail = () => {
  const { id } = useParams();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formOpen, setFormOpen] = useState(false);
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [materialDialogOpen, setMaterialDialogOpen] = useState(false);
  const [gradeDialogOpen, setGradeDialogOpen] = useState(false);
  const [certificateDialogOpen, setCertificateDialogOpen] = useState(false);
  const [selectedStudentForEnroll, setSelectedStudentForEnroll] = useState('');
  const [selectedStudentForGrade, setSelectedStudentForGrade] = useState('');
  const [selectedStudentForCert, setSelectedStudentForCert] = useState('');
  const [selectedLevelForCert, setSelectedLevelForCert] = useState('');
  
  // New session form state
  const [newSession, setNewSession] = useState({ title: '', date: '', duration_minutes: 90 });
  
  // New material form state
  const [newMaterial, setNewMaterial] = useState({ title: '', type: 'pdf', url: '', description: '' });
  const [uploadingFile, setUploadingFile] = useState(false);
  const materialFileRef = useRef(null);
  
  // New grade form state
  const [newGrade, setNewGrade] = useState({ evaluation_title: '', score: '', max_score: 20, comment: '' });

  // RBAC
  const isAdmin = user?.role === 'admin';
  const isRespPeda = user?.role === 'resp_peda';
  const isTeacher = user?.role === 'teacher';
  const isStudent = user?.role === 'student';
  const canEdit = isAdmin || isRespPeda;
  const canEnroll = isAdmin || isRespPeda;
  const canAddMaterial = isAdmin || isRespPeda || isTeacher;
  const canAddGrade = isAdmin || isRespPeda || isTeacher;
  const canIssueCert = isAdmin || isRespPeda;
  
  const statusLabels = statusLabelsFn(lang);

  // Queries
  const { data: course, isLoading, error } = useCourse(id);
  const { data: enrolledStudents } = useEnrolledStudents(id);
  const { data: sessions } = useSessions(id);
  const { data: materials } = useMaterials(id);
  const { data: grades } = useGrades(id);
  const { data: certificates } = useCertificates(null, id);
  const { data: allStudents } = useStudents({ per_page: 100 });

  // Mutations
  const enrollStudent = useEnrollStudent(id);
  const unenrollStudent = useUnenrollStudent(id);
  const createSession = useCreateSession(id);
  const deleteSession = useDeleteSession(id);
  const createMaterial = useCreateMaterial(id);
  const deleteMaterial = useDeleteMaterial(id);
  const createGrade = useCreateGrade(id);
  const issueCertificate = useIssueCertificate(id);

  // Get students not enrolled
  const enrolledIds = new Set(enrolledStudents?.map(s => s.id) || []);
  const availableStudents = allStudents?.students?.filter(s => !enrolledIds.has(s.id)) || [];

  const handleEnrollStudent = async () => {
    if (!selectedStudentForEnroll) return;
    try {
      await enrollStudent.mutateAsync(selectedStudentForEnroll);
      toast.success('Étudiant inscrit avec succès');
      setEnrollDialogOpen(false);
      setSelectedStudentForEnroll('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    }
  };

  const handleUnenrollStudent = async (studentId) => {
    try {
      await unenrollStudent.mutateAsync(studentId);
      toast.success('Étudiant désinscrit');
    } catch (error) {
      toast.error('Erreur lors de la désinscription');
    }
  };

  const handleCreateSession = async () => {
    if (!newSession.title || !newSession.date) {
      toast.error('Titre et date requis');
      return;
    }
    try {
      await createSession.mutateAsync({
        course_id: id,
        ...newSession,
        duration_minutes: parseInt(newSession.duration_minutes),
      });
      toast.success('Session créée avec succès');
      setSessionDialogOpen(false);
      setNewSession({ title: '', date: '', duration_minutes: 90 });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await deleteSession.mutateAsync(sessionId);
      toast.success('Session supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCreateMaterial = async () => {
    if (!newMaterial.title || !newMaterial.url) {
      toast.error('Titre et URL/fichier requis');
      return;
    }
    try {
      await createMaterial.mutateAsync({
        course_id: id,
        ...newMaterial,
      });
      toast.success('Matériau ajouté avec succès');
      setMaterialDialogOpen(false);
      setNewMaterial({ title: '', type: 'pdf', url: '', description: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    }
  };

  const handleMaterialFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/courses/upload-material`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setNewMaterial(prev => ({
        ...prev,
        url: res.data.url,
        title: prev.title || file.name.replace(/\.[^/.]+$/, '')
      }));
      toast.success(`Fichier "${file.name}" uploadé`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    try {
      await deleteMaterial.mutateAsync(materialId);
      toast.success('Matériau supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCreateGrade = async () => {
    if (!selectedStudentForGrade || !newGrade.evaluation_title || !newGrade.score) {
      toast.error('Tous les champs requis');
      return;
    }
    try {
      await createGrade.mutateAsync({
        course_id: id,
        student_id: selectedStudentForGrade,
        evaluation_title: newGrade.evaluation_title,
        score: parseFloat(newGrade.score),
        max_score: parseFloat(newGrade.max_score),
        comment: newGrade.comment,
      });
      toast.success('Note ajoutée avec succès');
      setGradeDialogOpen(false);
      setSelectedStudentForGrade('');
      setNewGrade({ evaluation_title: '', score: '', max_score: 20, comment: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    }
  };

  const handleIssueCertificate = async () => {
    if (!selectedStudentForCert || !selectedLevelForCert) {
      toast.error('Étudiant et niveau requis');
      return;
    }
    try {
      await issueCertificate.mutateAsync({
        course_id: id,
        student_id: selectedStudentForCert,
        level: selectedLevelForCert,
      });
      toast.success('Certificat généré avec succès');
      setCertificateDialogOpen(false);
      setSelectedStudentForCert('');
      setSelectedLevelForCert('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(lang === 'de' ? 'de-DE' : 'fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString(lang === 'de' ? 'de-DE' : 'fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">{lang === 'de' ? 'Kurs nicht gefunden' : 'Cours non trouvé'}</h2>
        <Button variant="outline" onClick={() => navigate('/courses')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {lang === 'de' ? 'Zurück zur Liste' : 'Retour à la liste'}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="course-detail-page">
      {/* Back button */}
      <Button variant="ghost" onClick={() => navigate('/courses')} className="mb-2">
        <ArrowLeft className="w-4 h-4 mr-2" />
        {lang === 'de' ? 'Zurück zu den Kursen' : 'Retour aux cours'}
      </Button>

      {/* Course Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">{course.name}</h1>
          <div className="flex items-center gap-2 mt-2">
            <Badge className={levelColors[course.level]}>{course.level}</Badge>
            <Badge className={statusColors[course.status]}>{statusLabels[course.status]}</Badge>
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <>
              <Button 
                variant="outline"
                onClick={() => navigate(`/admin/courses/${id}/content`)} 
                data-testid="manage-elearning-btn"
              >
                <Video className="w-4 h-4 mr-2" />
                {lang === 'de' ? 'E-Learning-Inhalt' : 'Contenu E-Learning'}
              </Button>
              <Button onClick={() => setFormOpen(true)} data-testid="edit-course-btn">
                {lang === 'de' ? 'Kurs bearbeiten' : 'Modifier le cours'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="w-full justify-start bg-muted/50 p-1 flex-wrap h-auto">
          <TabsTrigger value="overview">{lang === 'de' ? 'Übersicht' : 'Vue générale'}</TabsTrigger>
          <TabsTrigger value="sessions">{lang === 'de' ? 'Sitzungen' : 'Sessions'}</TabsTrigger>
          <TabsTrigger value="materials">{lang === 'de' ? 'Materialien' : 'Matériaux'}</TabsTrigger>
          {!isStudent && <TabsTrigger value="grades">{lang === 'de' ? 'Noten' : 'Notes'}</TabsTrigger>}
          {canIssueCert && <TabsTrigger value="certificates">{lang === 'de' ? 'Zertifikate' : 'Certificats'}</TabsTrigger>}
        </TabsList>

        {/* Tab: Vue générale */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Course Info */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" />
                  {lang === 'de' ? 'Informationen' : 'Informations'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {course.description && (
                  <p className="text-muted-foreground">{course.description}</p>
                )}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span>{course.enrolled_count} / {course.max_students} {lang === 'de' ? 'Studierende' : 'étudiants'}</span>
                  </div>
                  {course.schedule && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span>{course.schedule}</span>
                    </div>
                  )}
                  {course.teacher_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="bg-secondary text-white text-xs">
                          {course.teacher_name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <span>{course.teacher_name}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{lang === 'de' ? 'Sitzungen' : 'Sessions'}</span>
                    <span>{course.completed_sessions} / {course.sessions_count}</span>
                  </div>
                  <Progress 
                    value={course.sessions_count > 0 ? (course.completed_sessions / course.sessions_count) * 100 : 0} 
                    className="h-2" 
                  />
                </div>
              </CardContent>
            </Card>

            {/* Enrolled Students */}
            <Card className="border-0 shadow-sm lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-secondary" />
                    {lang === 'de' ? 'Eingeschriebene Studierende' : 'Étudiants inscrits'} ({enrolledStudents?.length || 0})
                  </CardTitle>
                  {canEnroll && (
                    <Dialog open={enrollDialogOpen} onOpenChange={setEnrollDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm">
                          <UserPlus className="w-4 h-4 mr-2" />
                          {lang === 'de' ? 'Einschreiben' : 'Inscrire'}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{lang === 'de' ? 'Studierende/n einschreiben' : 'Inscrire un étudiant'}</DialogTitle>
                          <DialogDescription>
                            {lang === 'de' ? 'Wählen Sie eine/n Studierende/n aus, der/die in diesen Kurs eingeschrieben werden soll' : 'Sélectionnez un étudiant à inscrire à ce cours'}
                          </DialogDescription>
                        </DialogHeader>
                        <Select value={selectedStudentForEnroll} onValueChange={setSelectedStudentForEnroll}>
                          <SelectTrigger>
                            <SelectValue placeholder={lang === 'de' ? 'Studierende/n auswählen' : 'Sélectionner un étudiant'} />
                          </SelectTrigger>
                          <SelectContent>
                            {availableStudents.map((student) => (
                              <SelectItem key={student.id} value={student.id}>
                                {student.first_name} {student.last_name} - {student.german_level}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setEnrollDialogOpen(false)}>{lang === 'de' ? 'Abbrechen' : 'Annuler'}</Button>
                          <Button onClick={handleEnrollStudent} disabled={enrollStudent.isPending}>
                            {enrollStudent.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === 'de' ? 'Einschreiben' : 'Inscrire')}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {enrolledStudents?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">{lang === 'de' ? 'Keine eingeschriebenen Studierenden' : 'Aucun étudiant inscrit'}</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{lang === 'de' ? 'Studierende/r' : 'Étudiant'}</TableHead>
                        <TableHead>{lang === 'de' ? 'Niveau' : 'Niveau'}</TableHead>
                        <TableHead>{lang === 'de' ? 'Anwesenheit' : 'Présence'}</TableHead>
                        <TableHead>{lang === 'de' ? 'Durchschnitt' : 'Moyenne'}</TableHead>
                        {canEnroll && <TableHead className="text-right">{lang === 'de' ? 'Aktionen' : 'Actions'}</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {enrolledStudents?.map((student) => (
                        <TableRow key={student.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="bg-primary text-white text-xs">
                                  {student.first_name[0]}{student.last_name[0]}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{student.first_name} {student.last_name}</p>
                                <p className="text-xs text-muted-foreground">{student.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={levelColors[student.german_level]}>{student.german_level}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Progress value={student.attendance_rate} className="h-2 w-16" />
                              <span className="text-xs">{student.attendance_rate}%</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {student.average_grade !== null ? (
                              <Badge variant="outline">{student.average_grade}%</Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          {canEnroll && (
                            <TableCell className="text-right">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleUnenrollStudent(student.id)}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Sessions */}
        <TabsContent value="sessions" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Sessions ({sessions?.length || 0})
                </CardTitle>
                {canEdit && (
                  <Dialog open={sessionDialogOpen} onOpenChange={setSessionDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvelle session
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Créer une session</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Titre *</Label>
                          <Input 
                            value={newSession.title}
                            onChange={(e) => setNewSession({...newSession, title: e.target.value})}
                            placeholder="Ex: Introduction au vocabulaire"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Date et heure *</Label>
                          <Input 
                            type="datetime-local"
                            value={newSession.date}
                            onChange={(e) => setNewSession({...newSession, date: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Durée (minutes)</Label>
                          <Input 
                            type="number"
                            value={newSession.duration_minutes}
                            onChange={(e) => setNewSession({...newSession, duration_minutes: e.target.value})}
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setSessionDialogOpen(false)}>{t('action_cancel', lang)}</Button>
                        <Button onClick={handleCreateSession} disabled={createSession.isPending}>
                          {createSession.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer'}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {sessions?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Aucune session planifiée</p>
              ) : (
                <div className="space-y-4">
                  {sessions?.map((session) => {
                    const isPast = new Date(session.date) < new Date();
                    return (
                      <div 
                        key={session.id} 
                        className={`flex items-center justify-between p-4 rounded-lg border ${isPast ? 'bg-muted/50' : 'bg-white'}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isPast ? 'bg-muted' : 'bg-primary/10'}`}>
                            <Calendar className={`w-6 h-6 ${isPast ? 'text-muted-foreground' : 'text-primary'}`} />
                          </div>
                          <div>
                            <p className="font-medium">{session.title}</p>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span>{formatDateTime(session.date)}</span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {session.duration_minutes} min
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {session.attendance_summary && (
                            <div className="flex items-center gap-2 text-sm">
                              <CheckCircle className="w-4 h-4 text-success" />
                              <span>{session.attendance_summary.present}</span>
                              <AlertCircle className="w-4 h-4 text-warning" />
                              <span>{session.attendance_summary.late}</span>
                              <XCircle className="w-4 h-4 text-destructive" />
                              <span>{session.attendance_summary.absent}</span>
                            </div>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => navigate(`/courses/${id}/session/${session.id}`)}
                          >
                            Voir
                          </Button>
                          {canEdit && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeleteSession(session.id)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Matériaux */}
        <TabsContent value="materials" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-accent" />
                  Matériaux pédagogiques ({materials?.length || 0})
                </CardTitle>
                {canAddMaterial && (
                  <Dialog open={materialDialogOpen} onOpenChange={setMaterialDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{t('course_detail_add_material', lang)}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Titre *</Label>
                          <Input 
                            value={newMaterial.title}
                            onChange={(e) => setNewMaterial({...newMaterial, title: e.target.value})}
                            placeholder="Ex: Support de cours - Leçon 1"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('partners_type', lang)}</Label>
                          <Select value={newMaterial.type} onValueChange={(v) => setNewMaterial({...newMaterial, type: v})}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pdf">PDF</SelectItem>
                              <SelectItem value="video">Vidéo</SelectItem>
                              <SelectItem value="exercise">Exercice</SelectItem>
                              <SelectItem value="link">Lien</SelectItem>
                              <SelectItem value="other">Autre</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Fichier ou URL *</Label>
                          <input
                            type="file"
                            ref={materialFileRef}
                            onChange={handleMaterialFileUpload}
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx,.pptx,.mp4,.mp3"
                          />
                          <div className="flex gap-2">
                            <Input 
                              value={newMaterial.url}
                              onChange={(e) => setNewMaterial({...newMaterial, url: e.target.value})}
                              placeholder="https://... ou uploadez un fichier"
                              className="flex-1"
                              readOnly={uploadingFile}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => materialFileRef.current?.click()}
                              disabled={uploadingFile}
                              data-testid="upload-material-btn"
                            >
                              {uploadingFile ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Upload className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                          {newMaterial.url && newMaterial.url.startsWith('/api/') && (
                            <p className="text-xs text-green-600 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> Fichier uploadé
                            </p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label>{t('form_description', lang)}</Label>
                          <Textarea 
                            value={newMaterial.description}
                            onChange={(e) => setNewMaterial({...newMaterial, description: e.target.value})}
                            rows={2}
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setMaterialDialogOpen(false)}>{t('action_cancel', lang)}</Button>
                        <Button onClick={handleCreateMaterial} disabled={createMaterial.isPending}>
                          {createMaterial.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ajouter'}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {materials?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Aucun matériau disponible</p>
              ) : (
                <div className="space-y-3">
                  {materials?.map((material) => {
                    const Icon = materialTypeIcons[material.type] || FileText;
                    return (
                      <div key={material.id} className="flex items-center justify-between p-3 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                            <Icon className="w-5 h-5 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="font-medium">{material.title}</p>
                            <p className="text-xs text-muted-foreground">
                              Par {material.uploaded_by_name} • {formatDate(material.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" asChild>
                            <a 
                              href={material.url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${material.url}` : material.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              {material.url.startsWith('/api/') ? (
                                <><Download className="w-4 h-4 mr-1" /> Télécharger</>
                              ) : (
                                <><ExternalLink className="w-4 h-4 mr-1" /> Ouvrir</>
                              )}
                            </a>
                          </Button>
                          {canAddMaterial && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeleteMaterial(material.id)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Notes */}
        {!isStudent && (
          <TabsContent value="grades" className="space-y-6">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Award className="w-5 h-5 text-warning" />
                    Notes & Évaluations
                  </CardTitle>
                  {canAddGrade && (
                    <Dialog open={gradeDialogOpen} onOpenChange={setGradeDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm">
                          <Plus className="w-4 h-4 mr-2" />
                          Nouvelle note
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{t('course_detail_add_grade', lang)}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label>Étudiant *</Label>
                            <Select value={selectedStudentForGrade} onValueChange={setSelectedStudentForGrade}>
                              <SelectTrigger>
                                <SelectValue placeholder="Sélectionner un étudiant" />
                              </SelectTrigger>
                              <SelectContent>
                                {enrolledStudents?.map((student) => (
                                  <SelectItem key={student.id} value={student.id}>
                                    {student.first_name} {student.last_name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Évaluation *</Label>
                            <Input 
                              value={newGrade.evaluation_title}
                              onChange={(e) => setNewGrade({...newGrade, evaluation_title: e.target.value})}
                              placeholder="Ex: Test unité 1"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Note *</Label>
                              <Input 
                                type="number"
                                step="0.5"
                                value={newGrade.score}
                                onChange={(e) => setNewGrade({...newGrade, score: e.target.value})}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Sur</Label>
                              <Input 
                                type="number"
                                value={newGrade.max_score}
                                onChange={(e) => setNewGrade({...newGrade, max_score: e.target.value})}
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>{t('form_comment', lang)}</Label>
                            <Textarea 
                              value={newGrade.comment}
                              onChange={(e) => setNewGrade({...newGrade, comment: e.target.value})}
                              rows={2}
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setGradeDialogOpen(false)}>{t('action_cancel', lang)}</Button>
                          <Button onClick={handleCreateGrade} disabled={createGrade.isPending}>
                            {createGrade.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ajouter'}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {grades?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Aucune note enregistrée</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('students_title', lang) === 'Studierende' ? 'Studierende/r' : 'Étudiant'}</TableHead>
                        <TableHead>Évaluation</TableHead>
                        <TableHead>Note</TableHead>
                        <TableHead>{t('form_comment', lang)}</TableHead>
                        <TableHead>{t('form_date', lang)}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {grades?.map((grade) => {
                        const student = enrolledStudents?.find(s => s.id === grade.student_id);
                        return (
                          <TableRow key={grade.id}>
                            <TableCell>
                              {student ? `${student.first_name} ${student.last_name}` : 'Inconnu'}
                            </TableCell>
                            <TableCell>{grade.evaluation_title}</TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {grade.score} / {grade.max_score}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {grade.comment || '-'}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDate(grade.created_at)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Tab: Certificats */}
        {canIssueCert && (
          <TabsContent value="certificates" className="space-y-6">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Award className="w-5 h-5 text-primary" />
                    Certificats ({certificates?.length || 0})
                  </CardTitle>
                  <Dialog open={certificateDialogOpen} onOpenChange={setCertificateDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        Générer
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Générer un certificat</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Étudiant *</Label>
                          <Select value={selectedStudentForCert} onValueChange={setSelectedStudentForCert}>
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionner un étudiant" />
                            </SelectTrigger>
                            <SelectContent>
                              {enrolledStudents?.map((student) => (
                                <SelectItem key={student.id} value={student.id}>
                                  {student.first_name} {student.last_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Niveau certifié *</Label>
                          <Select value={selectedLevelForCert} onValueChange={setSelectedLevelForCert}>
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionner un niveau" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="A1">A1</SelectItem>
                              <SelectItem value="A2">A2</SelectItem>
                              <SelectItem value="B1">B1</SelectItem>
                              <SelectItem value="B2">B2</SelectItem>
                              <SelectItem value="C1">C1</SelectItem>
                              <SelectItem value="C2">C2</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setCertificateDialogOpen(false)}>{t('action_cancel', lang)}</Button>
                        <Button onClick={handleIssueCertificate} disabled={issueCertificate.isPending}>
                          {issueCertificate.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Générer'}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {certificates?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Aucun certificat délivré</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('students_title', lang) === 'Studierende' ? 'Studierende/r' : 'Étudiant'}</TableHead>
                        <TableHead>{t('students_level', lang)}</TableHead>
                        <TableHead>{t('form_date', lang)}</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {certificates?.map((cert) => (
                        <TableRow key={cert.id}>
                          <TableCell>{cert.student_name}</TableCell>
                          <TableCell>
                            <Badge className={levelColors[cert.level]}>{cert.level}</Badge>
                          </TableCell>
                          <TableCell>{formatDate(cert.issued_at)}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="outline" size="sm" asChild>
                              <a href={`${process.env.REACT_APP_BACKEND_URL}${cert.pdf_url}`} target="_blank" rel="noopener noreferrer">
                                <Download className="w-4 h-4 mr-1" />
                                Télécharger
                              </a>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Edit Form Sheet */}
      <CourseForm 
        open={formOpen} 
        onClose={() => setFormOpen(false)} 
        course={course} 
      />
    </div>
  );
};

export default CourseDetail;
